import * as constants from '../../constants/CONSTANT';

const adminSchemaApi = {
  async fetchSchemas() {
    const response = await fetch(
      constants.API_BASE_URL + '/api/admin/company',
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
      }
    );

    if (!response.ok) {
      const error = new Error('Failed to fetch schemas');
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return Array.isArray(data?.records) ? data.records : [];
  },

  async createCompany(payload) {
    const response = await fetch(constants.API_BASE_URL + '/api/admin/company', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
      body: JSON.stringify(payload || {}),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to create company');
    return data?.record ?? null;
  },

  async updateCompany(id, payload) {
    const response = await fetch(constants.API_BASE_URL + `/api/admin/company/detail/${id}`, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
      body: JSON.stringify(payload || {}),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to update company');
    return data?.record ?? null;
  },

  async fetchCompanyById(id) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${id}`,
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return null;
      }
      const error = new Error('Failed to fetch company');
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return data?.record || null;
  },

  async fetchCompanyUsers(companyId) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${companyId}/users`,
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
      }
    );
    if (!response.ok) {
      if (response.status === 404) return [];
      throw new Error('Failed to fetch company users');
    }
    const data = await response.json();
    return Array.isArray(data?.records) ? data.records : [];
  },

  async setDefaultSchemaUser(companyId, userId) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${companyId}/default-schema-user`,
      {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
        body: JSON.stringify({ user_id: userId }),
      }
    );
    if (!response.ok) throw new Error('Failed to set default schema user');
    const data = await response.json();
    return data?.default_schema_user_id ?? null;
  },

  async fetchCompanyLicenseTypes(companyId) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${companyId}/license-types`,
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
      }
    );
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData?.message || 'Failed to fetch company license types');
    }
    const data = await response.json();
    return Array.isArray(data?.records) ? data.records : [];
  },

  async updateCompanyLicenseTypeEntitlements(companyId, companyLicenseTypeId, payload) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${companyId}/license-types/${companyLicenseTypeId}`,
      {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
        body: JSON.stringify(payload),
      }
    );
    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(errData?.message || 'Failed to update entitlements');
    }
    const data = await response.json();
    return data?.record ?? null;
  },

  async fetchSubscriptionsByCompanyId(companyId) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${companyId}/subscriptions`,
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
      }
    );

    if (!response.ok) {
      const error = new Error('Failed to fetch subscriptions');
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return Array.isArray(data?.records) ? data.records : [];
  },

  /**
   * Reconcile Admin roles' permission_config.tabs with active subscription + Admin license (standard tabs only;
   * custom entries preserved). Platform admin only.
   */
  async reconcileAdminTabs(companyId) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${companyId}/reconcile-admin-tabs`,
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
        body: '{}',
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to reconcile Admin role tabs');
    }
    return data;
  },

  async fetchLicenseTypes() {
    const response = await fetch(
      constants.API_BASE_URL + '/api/admin/company/license-types',
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
      }
    );

    if (!response.ok) {
      const error = new Error('Failed to fetch license types');
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return Array.isArray(data?.records) ? data.records : [];
  },

  /**
   * Fetch standard tabs. Only tabs with no config.package or with config.package in enabledPackages are returned.
   * @param {string[]|string} [enabledPackages] - If provided, only tabs whose config.package is in this list (or unset) are returned.
   */
  async fetchStandardTabs(enabledPackages) {
    let url = constants.API_BASE_URL + '/api/admin/company/standard-tabs';
    const list = Array.isArray(enabledPackages)
      ? enabledPackages.filter(Boolean).map(String)
      : typeof enabledPackages === 'string' && enabledPackages.trim()
        ? enabledPackages.split(',').map((p) => p.trim()).filter(Boolean)
        : [];
    if (list.length > 0) url += '?packages=' + encodeURIComponent(list.join(','));
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch standard tabs');
    const data = await response.json();
    return Array.isArray(data?.records) ? data.records : [];
  },

  /**
   * Fetch standard objects. Only objects with no config.package or with config.package in enabledPackages are returned.
   * @param {string[]|string} [enabledPackages] - If provided, only objects whose config.package is in this list (or unset) are returned. E.g. ['watconnect']. Can be comma-separated string.
   */
  async fetchStandardObjects(enabledPackages) {
    let url = constants.API_BASE_URL + '/api/admin/company/standard-objects';
    const list = Array.isArray(enabledPackages)
      ? enabledPackages.filter(Boolean).map(String)
      : typeof enabledPackages === 'string' && enabledPackages.trim()
        ? enabledPackages.split(',').map((p) => p.trim()).filter(Boolean)
        : [];
    if (list.length > 0) url += '?packages=' + encodeURIComponent(list.join(','));
    const response = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch standard objects');
    const data = await response.json();
    return Array.isArray(data?.records) ? data.records : [];
  },

  async fetchLicenseTypeById(id) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/license-types/${id}`,
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
      }
    );
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error('Failed to fetch license type');
    }
    const data = await response.json();
    return data?.record || null;
  },

  async createLicenseType(body) {
    const response = await fetch(constants.API_BASE_URL + '/api/admin/company/license-types', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
      body: JSON.stringify(body || {}),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to create license type');
    return data?.record || null;
  },

  async updateLicenseType(id, body) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/license-types/${id}`,
      {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
        body: JSON.stringify(body || {}),
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to update license type');
    return data?.record || null;
  },

  async deleteLicenseType(id) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/license-types/${id}`,
      {
        method: 'DELETE',
        mode: 'cors',
        headers: { Authorization: localStorage.getItem('adminToken') || '' },
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to delete license type');
    return true;
  },

  async fetchInvoicesByCompanyId(companyId) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${companyId}/invoices`,
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
      }
    );

    if (!response.ok) {
      const error = new Error('Failed to fetch invoices');
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return Array.isArray(data?.records) ? data.records : [];
  },

  async createInvoice(companyId, subscriptionId, payload) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${companyId}/subscriptions/${subscriptionId}/invoices`,
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      const error = new Error(errData?.message || 'Failed to create invoice');
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return data?.record || null;
  },

  async fetchInvoiceById(companyId, invoiceId) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${companyId}/invoices/${invoiceId}`,
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
      }
    );
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error('Failed to fetch invoice');
    }
    const data = await response.json();
    return data?.record || null;
  },

  async updateInvoice(companyId, invoiceId, payload) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${companyId}/invoices/${invoiceId}`,
      {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
        body: JSON.stringify(payload || {}),
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to update invoice');
    return data?.record || null;
  },

  async deleteInvoice(companyId, invoiceId) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${companyId}/invoices/${invoiceId}`,
      {
        method: 'DELETE',
        mode: 'cors',
        headers: { Authorization: localStorage.getItem('adminToken') || '' },
      }
    );
    const errData = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(errData?.message || 'Failed to delete invoice');
    return true;
  },

  async addLicenseToSubscription(companyId, subscriptionId, payload) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${companyId}/subscriptions/${subscriptionId}/licenses`,
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      const error = new Error(errData?.message || 'Failed to add license');
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return data?.record || null;
  },

  async updateLicense(companyId, subscriptionId, licenseId, payload) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${companyId}/subscriptions/${subscriptionId}/licenses/${licenseId}`,
      {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      const error = new Error(errData?.message || 'Failed to update license');
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return data?.record || null;
  },

  async deleteSubscription(companyId, subscriptionId) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${companyId}/subscriptions/${subscriptionId}`,
      {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
      }
    );

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      const error = new Error(errData?.message || 'Failed to delete subscription');
      error.status = response.status;
      throw error;
    }
    return true;
  },

  async createSubscription(companyId, payload) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${companyId}/subscriptions`,
      {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      const error = new Error(errData?.message || 'Failed to create subscription');
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return data?.record || null;
  },

  async updateSubscription(companyId, subscriptionId, payload) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/detail/${companyId}/subscriptions/${subscriptionId}`,
      {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      const error = new Error(errData?.message || 'Failed to update subscription');
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return data?.record || null;
  },

  async fetchConfig(tenantCode) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/${tenantCode}`,
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
      }
    );

    if (!response.ok) {
      const error = new Error('Failed to fetch config');
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return data?.config || null;
  },

  async saveConfig(tenantCode, config) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/${tenantCode}`,
      {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
        body: JSON.stringify({ config }),
      }
    );

    if (!response.ok) {
      const error = new Error('Failed to save config');
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return !!data?.success;
  },

  async fetchObjects(tenantCode) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/${tenantCode}/objects`,
      {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
      }
    );

    if (!response.ok) {
      const error = new Error('Failed to fetch objects');
      error.status = response.status;
      throw error;
    }

    const data = await response.json();
    return Array.isArray(data?.records) ? data.records : [];
  },

  // ---------- Pricebooks (admin) ----------
  async fetchPricebooks() {
    const response = await fetch(constants.API_BASE_URL + '/api/admin/pricebook', {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch pricebooks');
    const data = await response.json();
    return Array.isArray(data?.records) ? data.records : [];
  },

  async fetchPricebookById(id) {
    const response = await fetch(constants.API_BASE_URL + `/api/admin/pricebook/${id}`, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
    });
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error('Failed to fetch pricebook');
    }
    const data = await response.json();
    return data?.record || null;
  },

  async createPricebook(body) {
    const response = await fetch(constants.API_BASE_URL + '/api/admin/pricebook', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
      body: JSON.stringify(body || {}),
    });
    if (!response.ok) throw new Error((await response.json())?.message || 'Failed to create pricebook');
    const data = await response.json();
    return data?.record || null;
  },

  async updatePricebook(id, body) {
    const response = await fetch(constants.API_BASE_URL + `/api/admin/pricebook/${id}`, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
      body: JSON.stringify(body || {}),
    });
    if (!response.ok) throw new Error((await response.json())?.message || 'Failed to update pricebook');
    const data = await response.json();
    return data?.record || null;
  },

  async deletePricebook(id) {
    const response = await fetch(constants.API_BASE_URL + `/api/admin/pricebook/${id}`, {
      method: 'DELETE',
      mode: 'cors',
      headers: { Authorization: localStorage.getItem('adminToken') || '' },
    });
    if (!response.ok) throw new Error((await response.json())?.message || 'Failed to delete pricebook');
    return true;
  },

  async fetchPricebookEntries(pricebookId) {
    const response = await fetch(constants.API_BASE_URL + `/api/admin/pricebook/${pricebookId}/entries`, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch pricebook entries');
    const data = await response.json();
    return Array.isArray(data?.records) ? data.records : [];
  },

  async createPricebookEntry(pricebookId, body) {
    const response = await fetch(constants.API_BASE_URL + `/api/admin/pricebook/${pricebookId}/entries`, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
      body: JSON.stringify(body || {}),
    });
    if (!response.ok) throw new Error((await response.json())?.message || 'Failed to create entry');
    const data = await response.json();
    return data?.record || null;
  },

  async updatePricebookEntry(pricebookId, entryId, body) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/pricebook/${pricebookId}/entries/${entryId}`,
      {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
        body: JSON.stringify(body || {}),
      }
    );
    if (!response.ok) throw new Error((await response.json())?.message || 'Failed to update entry');
    const data = await response.json();
    return data?.record || null;
  },

  async deletePricebookEntry(pricebookId, entryId) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/pricebook/${pricebookId}/entries/${entryId}`,
      {
        method: 'DELETE',
        mode: 'cors',
        headers: { Authorization: localStorage.getItem('adminToken') || '' },
      }
    );
    if (!response.ok) throw new Error((await response.json())?.message || 'Failed to delete entry');
    return true;
  },

  // ---------- Plans (admin) ----------
  async fetchPlans() {
    const response = await fetch(constants.API_BASE_URL + '/api/admin/company/plans', {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch plans');
    const data = await response.json();
    return Array.isArray(data?.records) ? data.records : [];
  },

  async fetchPlanById(id) {
    const response = await fetch(constants.API_BASE_URL + `/api/admin/company/plans/${id}`, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
    });
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error('Failed to fetch plan');
    }
    const data = await response.json();
    return data?.record || null;
  },

  async createPlan(body) {
    const response = await fetch(constants.API_BASE_URL + '/api/admin/company/plans', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
      body: JSON.stringify(body || {}),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to create plan');
    return data?.record || null;
  },

  async updatePlan(id, body) {
    const response = await fetch(constants.API_BASE_URL + `/api/admin/company/plans/${id}`, {
      method: 'PUT',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
      body: JSON.stringify(body || {}),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to update plan');
    return data?.record || null;
  },

  async deletePlan(id) {
    const response = await fetch(constants.API_BASE_URL + `/api/admin/company/plans/${id}`, {
      method: 'DELETE',
      mode: 'cors',
      headers: { Authorization: localStorage.getItem('adminToken') || '' },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to delete plan');
    return true;
  },

  async fetchPlanLicenses(planId) {
    const response = await fetch(constants.API_BASE_URL + `/api/admin/company/plans/${planId}/licenses`, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch plan licenses');
    const data = await response.json();
    return Array.isArray(data?.records) ? data.records : [];
  },

  async createPlanLicense(planId, body) {
    const response = await fetch(constants.API_BASE_URL + `/api/admin/company/plans/${planId}/licenses`, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
      body: JSON.stringify(body || {}),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to add plan license');
    return data?.record || null;
  },

  async updatePlanLicense(planId, licenseId, body) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/plans/${planId}/licenses/${licenseId}`,
      {
        method: 'PUT',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: localStorage.getItem('adminToken') || '',
        },
        body: JSON.stringify(body || {}),
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to update plan license');
    return data?.record || null;
  },

  async deletePlanLicense(planId, licenseId) {
    const response = await fetch(
      constants.API_BASE_URL + `/api/admin/company/plans/${planId}/licenses/${licenseId}`,
      {
        method: 'DELETE',
        mode: 'cors',
        headers: { Authorization: localStorage.getItem('adminToken') || '' },
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to delete plan license');
    return true;
  },

  async fetchPlanPricebookEntries(planId) {
    const response = await fetch(constants.API_BASE_URL + `/api/admin/company/plans/${planId}/pricebook-entries`, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch plan pricebook entries');
    const data = await response.json();
    return Array.isArray(data?.records) ? data.records : [];
  },

  /** Fetch pricebooks that have at least one pricebook_entry for the given plan. */
  async fetchPricebooksByPlanId(planId) {
    if (!planId) return [];
    const response = await fetch(constants.API_BASE_URL + `/api/admin/company/plans/${planId}/pricebooks`, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
    });
    if (!response.ok) throw new Error('Failed to fetch pricebooks for plan');
    const data = await response.json();
    return Array.isArray(data?.records) ? data.records : [];
  },

  // --- Schema migration (source schema → company target schema) ---
  async fetchMigrationSchemas() {
    const response = await fetch(constants.API_BASE_URL + '/api/admin/migration/schemas', {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to fetch schemas');
    return Array.isArray(data?.data) ? data.data : [];
  },

  async fetchMigrationPlan(companyId, sourceSchema) {
    const params = new URLSearchParams({ companyId, sourceSchema });
    const response = await fetch(constants.API_BASE_URL + `/api/admin/migration/plan?${params}`, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to get migration plan');
    return data?.data ?? null;
  },

  async fetchMigrationTargetUsers(targetSchema) {
    if (!targetSchema) return [];
    const params = new URLSearchParams({ targetSchema });
    const response = await fetch(constants.API_BASE_URL + `/api/admin/migration/target-users?${params}`, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to get target users');
    return Array.isArray(data?.data) ? data.data : [];
  },

  async fetchStandardApps(sourceSchema, targetSchema = null) {
    const params = new URLSearchParams({ sourceSchema });
    if (targetSchema) params.set('targetSchema', targetSchema);
    const response = await fetch(constants.API_BASE_URL + `/api/admin/migration/standard-apps?${params}`, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to fetch standard apps');
    return Array.isArray(data?.data) ? data.data : [];
  },

  async createMigrationSchema(companyId) {
    const response = await fetch(constants.API_BASE_URL + '/api/admin/migration/create-schema', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
      body: JSON.stringify({ companyId }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Failed to create schema');
    return data;
  },

  async runMigration(companyId, sourceSchema, useDelta = true, selectedAppIds = [], selectedTargetUserId = null) {
    const body = {
      companyId,
      sourceSchema,
      useDelta,
      ...(Array.isArray(selectedAppIds) && selectedAppIds.length > 0 ? { selectedAppIds } : {}),
      ...(selectedTargetUserId ? { selectedTargetUserId } : {}),
    };
    const response = await fetch(constants.API_BASE_URL + '/api/admin/migration/run', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Migration failed');
    return data?.data ?? null;
  },

  async compareMigrationFieldDelta(companyId, sourceSchema, objectNames, singleTable = null, selectedTargetUserId = null) {
    const body = { companyId, sourceSchema, objectNames };
    if (singleTable && String(singleTable).trim()) {
      body.singleTable = String(singleTable).trim();
    }
    if (selectedTargetUserId && String(selectedTargetUserId).trim()) {
      body.selectedTargetUserId = String(selectedTargetUserId).trim();
    }
    const response = await fetch(constants.API_BASE_URL + '/api/admin/migration/field-delta/compare', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Field compare failed');
    return data?.data ?? null;
  },

  async migrateMigrationFieldDelta(
    companyId,
    sourceSchema,
    items,
    selectedTargetUserId,
    includeLookupRelationships = true,
    syncSafe = {}
  ) {
    const telecmiSyncSafeColumns = Array.isArray(syncSafe.telecmiSyncSafeColumns)
      ? syncSafe.telecmiSyncSafeColumns
      : [];
    const userTableSyncSafeColumns = Array.isArray(syncSafe.userTableSyncSafeColumns)
      ? syncSafe.userTableSyncSafeColumns
      : [];
    const response = await fetch(constants.API_BASE_URL + '/api/admin/migration/field-delta/migrate', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
      body: JSON.stringify({
        companyId,
        sourceSchema,
        items,
        selectedTargetUserId,
        includeLookupRelationships,
        ...(telecmiSyncSafeColumns.length > 0 ? { telecmiSyncSafeColumns } : {}),
        ...(userTableSyncSafeColumns.length > 0 ? { userTableSyncSafeColumns } : {}),
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'Field migration failed');
    return data?.data ?? null;
  },

  async syncUserMigrationSafeFields(companyId, sourceSchema, selectedTargetUserId) {
    const response = await fetch(constants.API_BASE_URL + '/api/admin/migration/user-table/sync-safe', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
      body: JSON.stringify({ companyId, sourceSchema, selectedTargetUserId }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'User table sync failed');
    return data?.data ?? null;
  },

  async syncTelecmiMigrationSafeFields(companyId, sourceSchema, selectedTargetUserId) {
    const response = await fetch(constants.API_BASE_URL + '/api/admin/migration/telecmi-setting/sync-safe', {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        Authorization: localStorage.getItem('adminToken') || '',
      },
      body: JSON.stringify({ companyId, sourceSchema, selectedTargetUserId }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data?.message || 'TeleCMI sync failed');
    return data?.data ?? null;
  },
};

export default adminSchemaApi;
